import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { setRealtimeDebug } from '@ngsos/portal-core/realtime';

import { initSessionRecording } from '@/analytics/sessionRecording';
import { initI18n } from '@/i18n';
import { setAuthSession } from '@/api/auth';
import { configureApiBaseUrls } from '@/api/baseUrls';
import App from '@/app/App';
import { createUserManager } from '@/app/auth/oidc';
import { createWebAuthSession } from '@/auth/webAuthSession';
import { restoreExpiredSessionBeforeFirstRender } from '@/auth/restoreSession';
import { showBootstrapError } from '@/app/bootstrapError';
import { fetchNgTopology } from '@/app/topology';
import { installUnhandledErrorReporting } from '@/lib/errors/unhandledErrors';

export async function bootstrap(): Promise<void> {
  const container = document.getElementById('app');
  if (!container) {
    throw new Error('Root element #app not found');
  }

  try {
    initSessionRecording();
    installUnhandledErrorReporting();

    const topology = await fetchNgTopology();

    const userManager = createUserManager(topology);

    await initI18n();

    configureApiBaseUrls({
      emsServerUrl: topology.emsServerUrl,
      eedServerUrl: topology.eedServerUrl,
    });
    const authSession = createWebAuthSession(userManager);
    setAuthSession(authSession);
    setRealtimeDebug(import.meta.env.DEV);

    await restoreExpiredSessionBeforeFirstRender(authSession);

    createRoot(container).render(
      <StrictMode>
        <App topology={topology} userManager={userManager} />
      </StrictMode>,
    );
  } catch (error) {
    showBootstrapError(error);
  }
}
